import { useState, useMemo, useEffect } from "react";
import { api, extractError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileText, Send, CheckCircle2, Euro, Users, MessageSquare, Building2,
  Sparkles, RotateCcw, Upload, Trash2, Camera, ExternalLink, Image as ImageIcon,
  ChevronDown, XCircle, Trophy, ClipboardCheck, Receipt, UserCheck,
  Pencil, Download, Maximize2, X, ChevronLeft, ChevronRight
} from "lucide-react";
import { LeadSelector } from "./EventProductionTab";
import { fmtFR } from "@/lib/dates";
import { toISODate } from "@/lib/time";

const API = process.env.REACT_APP_BACKEND_URL || "";
const fileUrl = (fileId) => `${API}/api/files/${fileId}`;

// =============== ROW helper (feux de validation) ================
function Row({ ok, label, onToggle, extra, testId }) {
  return (
    <label className={`flex items-center gap-2 rounded-lg border px-3 py-2 select-none cursor-pointer ${ok ? "border-emerald-400 bg-emerald-500/15" : "border-rose-500/50 bg-rose-500/15"}`} data-testid={testId}>
      <span className={`inline-block h-3 w-3 rounded-full ${ok ? "bg-emerald-500" : "bg-rose-400"}`} />
      <input type="checkbox" checked={!!ok} onChange={e => onToggle(e.target.checked)} className="sr-only" />
      <span className={`text-sm ${ok ? "text-emerald-200 font-semibold" : "text-rose-200"}`}>{label}</span>
      <span className="ml-auto text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{ok ? "OK" : "À vérifier"}</span>
      {extra}
    </label>
  );
}

// =============== SECTION 1 : Facture client (miroir du devis) ================
function ClientInvoiceSection({ event, reload }) {
  const inv = event.postprod?.client_invoice || {};
  const versions = inv.versions || [];
  const cp = event.checkpoints || {};
  const facturePret = !!cp.facture_pret?.done;
  const invoiceReviewPending = !!event.invoice_review_pending;

  const [costsOk, setCostsOk] = useState(!!inv.axonaut_costs_ok);
  const [prodsOk, setProdsOk] = useState(!!inv.axonaut_products_ok);
  const [folderOk, setFolderOk] = useState(!!inv.axonaut_folder_ok);
  const [amount, setAmount] = useState(inv.amount ?? inv.amount_ht ?? "");
  const [note, setNote] = useState(inv.note || "");
  const [sendDialog, setSendDialog] = useState({ open: false, sent_at: toISODate(new Date()), amount: "", note: "", files: [] });
  const [noteEdit, setNoteEdit] = useState({});

  useEffect(() => {
    setCostsOk(!!inv.axonaut_costs_ok);
    setProdsOk(!!inv.axonaut_products_ok);
    setFolderOk(!!inv.axonaut_folder_ok);
    setAmount(inv.amount ?? inv.amount_ht ?? "");
    setNote(inv.note || "");
    // eslint-disable-next-line
  }, [event.id]);

  const patchInv = async (patch) => {
    try {
      await api.patch(`/events/${event.id}/client-invoice`, patch);
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };
  const toggleLight = (key, next) => {
    if (key === "axonaut_costs_ok") setCostsOk(next);
    if (key === "axonaut_products_ok") setProdsOk(next);
    if (key === "axonaut_folder_ok") setFolderOk(next);
    patchInv({ [key]: next });
  };
  const setCheckpoint = async (key, done) => {
    try {
      await api.patch(`/events/${event.id}/checkpoint`, { key, done });
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };
  const requestReview = async () => {
    if (!confirm("Envoyer une demande de validation de la facture à l'administrateur ?")) return;
    try { await api.post(`/events/${event.id}/invoice-review-request`); toast.success("Demande envoyée à l'administrateur"); await reload(true); }
    catch (e) { toast.error(extractError(e)); }
  };
  const cancelReview = async () => {
    if (!confirm("Annuler la demande de validation ? La carte repasse en « Facture à faire ».")) return;
    try { await api.delete(`/events/${event.id}/invoice-review-request`); toast.success("Annulée"); await reload(true); }
    catch (e) { toast.error(extractError(e)); }
  };
  const sendNewVersion = async () => {
    if (!sendDialog.files.length) { toast.error("Ajoutez au moins un PDF"); return; }
    try {
      const fd = new FormData();
      if (sendDialog.sent_at) fd.append("sent_at", sendDialog.sent_at);
      if (sendDialog.amount !== "") fd.append("amount", sendDialog.amount);
      if (sendDialog.note) fd.append("note", sendDialog.note);
      sendDialog.files.forEach(f => fd.append("files", f));
      const nextV = versions.length + 1;
      await api.post(`/events/${event.id}/client-invoice/versions/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Facture envoyée — version v${nextV} archivée`);
      setSendDialog({ open: false, sent_at: toISODate(new Date()), amount: "", note: "", files: [] });
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };
  const deleteVersion = async (v) => {
    if (!confirm(`Supprimer la version ${v.version} de l'historique ?`)) return;
    try { await api.delete(`/events/${event.id}/client-invoice/versions/${v.version}`); toast.success("Version supprimée"); await reload(true); }
    catch (e) { toast.error(extractError(e)); }
  };
  const saveVersionNote = async (v) => {
    const nt = (noteEdit[v.version] ?? "").trim();
    try { await api.patch(`/events/${event.id}/client-invoice/versions/${v.version}`, { note: nt }); toast.success("Note enregistrée"); setNoteEdit(prev => { const cp = { ...prev }; delete cp[v.version]; return cp; }); await reload(true); }
    catch (e) { toast.error(extractError(e)); }
  };
  const markPaid = async () => {
    if (!confirm("Confirmer que le client a réglé la facture ? L'étape « Facture payée » sera cochée automatiquement.")) return;
    try { await api.post(`/events/${event.id}/client-invoice/paid`, {}); toast.success("Facture marquée payée 🎉"); await reload(true); }
    catch (e) { toast.error(extractError(e)); }
  };
  const unmarkPaid = async () => {
    if (!confirm("Annuler la validation du paiement client ?")) return;
    try { await api.post(`/events/${event.id}/client-invoice/unpaid`); toast.success("Paiement décoché"); await reload(true); }
    catch (e) { toast.error(extractError(e)); }
  };

  return (
    <Card className="border-border/60 shadow-warm" data-testid="postprod-client-invoice">
      <CardContent className="p-5 space-y-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
          <Building2 className="h-3 w-3 text-primary" /> Facture client
        </div>

        {/* Feux Axonaut + validation interne */}
        <div className="rounded-xl border border-border/60 bg-muted p-3 space-y-2">
          <Row ok={costsOk} label="Tous les coûts de revient sont mis à jour dans la facture Axonaut" onToggle={v => toggleLight("axonaut_costs_ok", v)} testId="axonaut-inv-costs" />
          <Row ok={prodsOk} label="Toutes les prestations supplémentaires par rapport au devis initial validé sont rajoutés et les quantités mises à jour dans la facture" onToggle={v => toggleLight("axonaut_products_ok", v)} testId="axonaut-inv-products" />
          <Row ok={folderOk} label="La facture a été mise dans le dossier client/factures" onToggle={v => toggleLight("axonaut_folder_ok", v)} testId="axonaut-inv-folder" />
          {!facturePret && !invoiceReviewPending && (
            <Button size="sm" variant="outline" disabled={!costsOk || !prodsOk || !folderOk}
              className="rounded-full border-2 border-primary text-primary hover:bg-primary/5 self-start font-semibold disabled:opacity-50"
              onClick={requestReview} data-testid="request-invoice-review-btn"
              title={(!costsOk || !prodsOk || !folderOk) ? "Passez d'abord les 3 feux au vert" : "Notifier l'administrateur pour validation"}>
              <Send className="h-3.5 w-3.5 mr-1" /> Envoyer la facture en validation auprès du responsable
            </Button>
          )}
          {!facturePret && invoiceReviewPending && (
            <Button size="sm" variant="outline" className="rounded-full border-2 border-amber-500 text-amber-200 hover:bg-amber-500/15 self-start font-semibold"
              onClick={cancelReview} data-testid="cancel-invoice-review-btn">
              ↩︎ Annuler la demande de validation
            </Button>
          )}
          <Row ok={facturePret} label="Facture prête à envoyer (validation interne)" onToggle={v => setCheckpoint("facture_pret", v)} testId="facture-pret-toggle"
            extra={invoiceReviewPending && !facturePret ? <Badge className="rounded-full bg-secondary text-secondary-foreground border-secondary text-[10px]">⏳ En attente de validation</Badge> : (facturePret && cp.facture_pret?.date ? <span className="text-[11px] text-emerald-200">Coché le {cp.facture_pret.date}</span> : null)} />
        </div>

        {/* Envoi d'une nouvelle version */}
        {facturePret && !inv.accepted && (
          <div className="rounded-xl border border-primary/40 bg-primary/5 p-3 flex flex-wrap items-center gap-2">
            <div className="text-xs font-semibold text-primary">Prêt à envoyer au client ?</div>
            <Button size="sm" className="rounded-full bg-primary shadow-glow ml-auto" onClick={() => setSendDialog(d => ({ ...d, open: true, amount: amount ?? "" }))} data-testid="send-invoice-btn">
              <Send className="h-3.5 w-3.5 mr-1" /> {versions.length ? `Envoyer version v${versions.length + 1}` : "Envoyer la facture (v1)"}
            </Button>
          </div>
        )}

        {/* Historique versions */}
        {versions.length > 0 && (
          <div className="rounded-xl border border-border/60 bg-card p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
              <FileText className="h-3 w-3 text-primary" /> Historique des versions envoyées ({versions.length})
            </div>
            {versions.map((v) => {
              const isLatest = v.version === versions[versions.length - 1]?.version;
              const isAccepted = inv.accepted && inv.accepted_version === v.version;
              return (
                <div key={v.version} className={`rounded-lg border p-2.5 ${isAccepted ? "border-primary bg-primary/5" : "border-border/50 bg-card"}`} data-testid={`inv-version-${v.version}`}>
                  <div className="flex items-start gap-2">
                    <Badge className={`rounded-full text-[10px] ${isAccepted ? "bg-primary text-primary-foreground" : "bg-muted"}`}>{v.version}</Badge>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                        {Number(v.amount || 0).toLocaleString("fr-FR")} € HT
                        {isAccepted && <span className="text-xs text-primary font-bold flex items-center gap-1"><Trophy className="h-3 w-3" /> Payée</span>}
                        {!isAccepted && isLatest && <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Courante</span>}
                      </div>
                      <div className="text-xs text-muted-foreground tabular-nums flex flex-wrap gap-3 mt-0.5">
                        <span>Envoyée : {v.sent_at || "—"}</span>
                        {v.follow_up_at && <span>Relance : {v.follow_up_at}</span>}
                      </div>
                      {v.note && noteEdit[v.version] === undefined && <div className="text-xs italic text-muted-foreground mt-1">« {v.note} »</div>}
                      {!isAccepted && v.version !== "v1" && (
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <Input value={noteEdit[v.version] ?? v.note ?? ""} onChange={e => setNoteEdit(prev => ({ ...prev, [v.version]: e.target.value }))} placeholder="Ex : + refacturation location" className="h-7 text-xs" />
                          {noteEdit[v.version] !== undefined && noteEdit[v.version] !== (v.note || "") && (
                            <Button size="sm" variant="outline" className="h-7 px-2 rounded-full border-primary/40 text-primary hover:bg-primary/5" onClick={() => saveVersionNote(v)}>
                              <CheckCircle2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      )}
                      {v.files?.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {v.files.map(f => (
                            <a key={f.id} href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-primary underline hover:no-underline">
                              <ExternalLink className="h-3 w-3" /> {f.name}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    {isLatest && !isAccepted && (
                      <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10 h-7 w-7" onClick={() => deleteVersion(v)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Bloc Facture payée */}
        {versions.length > 0 && (
          <div className={`rounded-xl border-2 p-3 flex flex-wrap items-center gap-3 ${inv.accepted ? "border-primary bg-primary/10" : "border-secondary bg-secondary/10"}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Réponse client à la facture</div>
              {inv.accepted ? (
                <div className="mt-1 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <div>
                    <div className="font-brand text-lg text-primary">Facture payée</div>
                    <div className="text-xs text-muted-foreground">Le {inv.accepted_at} — étape « Facture payée » cochée automatiquement</div>
                  </div>
                </div>
              ) : <div className="text-sm text-muted-foreground italic">En attente — cliquez dès que le client a réglé.</div>}
            </div>
            {inv.accepted ? (
              <Button variant="outline" size="sm" className="rounded-full" onClick={unmarkPaid}><RotateCcw className="h-3.5 w-3.5 mr-1" />Annuler</Button>
            ) : (
              <Button size="lg" className="rounded-full bg-primary shadow-glow" onClick={markPaid} data-testid="mark-invoice-paid">
                <Sparkles className="h-4 w-4 mr-2" /> Facture payée
              </Button>
            )}
          </div>
        )}

        <div>
          <Label className="text-xs">Note (interne)</Label>
          <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} onBlur={() => patchInv({ note })} />
        </div>

        <Dialog open={sendDialog.open} onOpenChange={o => setSendDialog(d => ({ ...d, open: o }))}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>Envoi facture — version v{versions.length + 1}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Date d'envoi</Label><Input type="date" value={sendDialog.sent_at} onChange={e => setSendDialog(d => ({ ...d, sent_at: e.target.value }))} /></div>
              <div><Label className="text-xs">Montant HT (€)</Label><Input type="number" step="0.01" value={sendDialog.amount} onChange={e => setSendDialog(d => ({ ...d, amount: e.target.value }))} /></div>
              {versions.length > 0 && <div><Label className="text-xs">Note version (facultatif)</Label><Input value={sendDialog.note} onChange={e => setSendDialog(d => ({ ...d, note: e.target.value }))} placeholder="Ex : + refacturation location scène" /></div>}
              <div>
                <Label className="text-xs">PDF facture * (un ou plusieurs)</Label>
                <label className="mt-1 rounded-xl border-2 border-dashed border-primary/40 p-3 flex items-center justify-center gap-2 cursor-pointer bg-primary/5 hover:bg-primary/10">
                  <Upload className="h-4 w-4 text-primary" />
                  <span className="text-xs">{sendDialog.files.length ? `${sendDialog.files.length} fichier(s) sélectionné(s)` : "Cliquez pour joindre les PDF"}</span>
                  <input type="file" accept="application/pdf" multiple className="hidden" onChange={e => setSendDialog(d => ({ ...d, files: Array.from(e.target.files || []) }))} />
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSendDialog(d => ({ ...d, open: false }))}>Annuler</Button>
              <Button className="bg-primary" onClick={sendNewVersion} data-testid="confirm-send-invoice"><Send className="h-3.5 w-3.5 mr-1" /> Envoyer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// =============== SECTION 2 : Prestataires factures Kanban 3-columns ================
function ProviderInvoicesKanban({ event, reload }) {
  const validated = useMemo(() => {
    const all = [
      ...(event.provider_requests || []).map(r => ({ ...r, _kind: "provider" })),
      ...(event.artist_requests || []).map(r => ({ ...r, _kind: "artist" })),
    ];
    return all.filter(r => {
      if (r.offer_decision !== "validated" || !r.received) return false;
      // Explicitement payé sur déclaration (nouveau champ) → exclusion
      if (r.payment_mode === "declaration") return false;
      // Legacy : flag manuel dans extras.invoice
      if (((r.extras || {}).invoice || {}).paid_via_declaration) return false;
      return true;
    });
  }, [event]);

  const patchExtras = async (r, patch) => {
    try {
      const merged = { ...(r.extras?.invoice || {}), ...patch };
      await api.patch(`/events/${event.id}/requests/${r.id}/extras?kind=${r._kind}`, { invoice: merged });
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };

  const setStatus = (r, status) => {
    const dateKey = status === "recue" ? "received_at" : (status === "sur_axonaut" ? "axonaut_at" : (status === "payee" ? "paid_at" : "reset_at"));
    return patchExtras(r, { status, [dateKey]: toISODate(new Date()) });
  };
  const setAmount = (r, amount_ht) => patchExtras(r, { amount_ht: amount_ht === "" ? null : parseFloat(amount_ht) });
  const togglePaidViaDeclaration = async (r) => {
    if (!confirm("Marquer ce prestataire/artiste comme payé exclusivement via déclaration ? Il n'apparaîtra plus dans ce Kanban.")) return;
    await patchExtras(r, { paid_via_declaration: true });
  };

  const cols = [
    { key: "en_attente",  label: "Facture en attente",       tint: "border-rose-500/50 bg-rose-500/15" },
    { key: "recue",       label: "Facture reçue",            tint: "border-amber-500/50 bg-amber-500/15" },
    { key: "sur_axonaut", label: "Facture mise sur Axonaut", tint: "border-violet-500/50 bg-violet-500/15" },
    { key: "payee",       label: "Facture payée",            tint: "border-emerald-400 bg-emerald-500/15" },
  ];
  const cardsByCol = cols.reduce((acc, c) => { acc[c.key] = validated.filter(r => ((r.extras?.invoice?.status) || "en_attente") === c.key); return acc; }, {});

  return (
    <Card className="border-border/60 shadow-warm" data-testid="postprod-provider-invoices">
      <CardContent className="p-5 space-y-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
          <Receipt className="h-3 w-3 text-primary" /> Prestataires & Artistes à payer sur facture
          <Badge variant="outline" className="rounded-full ml-2">{validated.length}</Badge>
        </div>
        {validated.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-4 text-center">Aucun prestataire/artiste validé pour cet événement.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {cols.map(c => (
              <div key={c.key} className={`rounded-xl border-2 ${c.tint} p-3 min-h-[200px]`} data-testid={`prov-inv-col-${c.key}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold mb-2 flex items-center justify-between">
                  <span>{c.label}</span>
                  <Badge variant="outline" className="rounded-full bg-card">{cardsByCol[c.key].length}</Badge>
                </div>
                <div className="space-y-2">
                  {cardsByCol[c.key].length === 0 && <div className="text-xs italic text-muted-foreground">—</div>}
                  {cardsByCol[c.key].map(r => {
                    const inv = r.extras?.invoice || {};
                    const invAmount = inv.amount_ht != null ? inv.amount_ht : r.offer_amount;
                    const dep = r.extras?.deposit || {};
                    const depAmt = dep.status === "paye" ? (dep.mode === "amount" ? Number(dep.amount || 0) : Number(r.offer_amount || 0) * Number(dep.percent || 0) / 100) : 0;
                    const remaining = Math.max(0, Number(invAmount || 0) - depAmt);
                    return (
                      <div key={r.id} className="rounded-lg border border-border/60 bg-card p-2.5 text-xs space-y-1.5" data-testid={`prov-inv-card-${r.id}`}>
                        <div className="flex items-start gap-1.5">
                          <Badge className="rounded-full bg-muted text-[9px]">{r._kind === "artist" ? "Artiste" : "Prestataire"}</Badge>
                          <div className="font-semibold flex-1 min-w-0 truncate">{r.name}</div>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => togglePaidViaDeclaration(r)} title="Payé exclusivement par déclaration → retirer">
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        {r.category && <div className="text-[10px] text-muted-foreground">{r.category}</div>}
                        <div className="flex items-center justify-between text-[11px]">
                          <span>Montant HT :</span>
                          <Input type="number" step="0.01" defaultValue={invAmount ?? ""} onBlur={e => e.target.value !== String(invAmount ?? "") && setAmount(r, e.target.value)} className="h-6 w-24 text-right text-[11px] tabular-nums" />
                        </div>
                        {depAmt > 0 && (
                          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                            <span>Acompte payé :</span><span className="tabular-nums">− {depAmt.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} €</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between text-[11px] font-bold text-primary">
                          <span>Restant :</span><span className="tabular-nums">{remaining.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} €</span>
                        </div>
                        <div className="flex gap-1 pt-1">
                          {cols.filter(x => x.key !== c.key).map(target => (
                            <Button key={target.key} size="sm" variant="outline" className="h-6 px-2 text-[10px] flex-1" onClick={() => setStatus(r, target.key)} data-testid={`move-${r.id}-${target.key}`}>
                              → {target.label.replace("Facture ", "")}
                            </Button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =============== SECTION 3 : Déclarations mini-Kanban filtrées ================
const DECL_PHASES = [
  { v: "contrat_a_faire",     label: "Contrat à faire",     tint: "border-slate-300 bg-muted" },
  { v: "dpae_faite",          label: "DPAE faite",          tint: "border-amber-500/50 bg-amber-500/15" },
  { v: "envoye",              label: "Contrat envoyé",      tint: "border-blue-500/50 bg-blue-500/15" },
  { v: "signe",               label: "Contrat signé",       tint: "border-violet-500/50 bg-violet-500/15" },
  { v: "paie_envoyee",        label: "Paie envoyée",        tint: "border-fuchsia-500/50 bg-fuchsia-500/15" },
  { v: "salaire_paye",        label: "Salaire payé",        tint: "border-emerald-400 bg-emerald-500/15" },
];
function DeclarationsMiniKanban({ event, reload }) {
  const declarations = event.linked_declarations || [];
  const patchStatus = async (d, phase) => {
    try { await api.patch(`/declarations/${d.id}/status`, { status: phase }); toast.success("Statut mis à jour"); await reload(true); }
    catch (e) { toast.error(extractError(e)); }
  };
  const patchInvoice = async (d) => {
    try { await api.patch(`/declarations/${d.id}/invoice`, { invoice_paid: !d.invoice_paid, invoice_sent: !d.invoice_paid ? true : d.invoice_sent }); await reload(true); }
    catch (e) { toast.error(extractError(e)); }
  };
  const byPhase = DECL_PHASES.reduce((acc, p) => { acc[p.v] = declarations.filter(d => (d.status || "contrat_a_faire") === p.v); return acc; }, {});

  return (
    <Card className="border-border/60 shadow-warm" data-testid="postprod-decl-kanban">
      <CardContent className="p-5 space-y-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
          <UserCheck className="h-3 w-3 text-primary" /> Artistes & Techniciens payés par déclaration
          <Badge variant="outline" className="rounded-full ml-2">{declarations.length}</Badge>
        </div>
        {declarations.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-4 text-center">Aucune déclaration liée à cet événement.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {DECL_PHASES.map(p => (
              <div key={p.v} className={`rounded-xl border-2 ${p.tint} p-2 min-h-[160px]`}>
                <div className="text-[9px] uppercase tracking-widest font-bold mb-2 flex items-center justify-between">
                  <span className="truncate">{p.label}</span>
                  <Badge variant="outline" className="rounded-full bg-card text-[9px] shrink-0">{byPhase[p.v].length}</Badge>
                </div>
                <div className="space-y-1.5">
                  {byPhase[p.v].map(d => (
                    <div key={d.id} className="rounded-md border border-border/60 bg-card p-1.5 text-[11px] space-y-0.5" data-testid={`decl-card-${d.id}`}>
                      <div className="font-semibold truncate">{d.intermittent?.first_name} {d.intermittent?.last_name || d.intermittent?.stage_name}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{d.role || "—"}</div>
                      <div className="tabular-nums text-primary font-bold">{Number(d.cost_ht_client || 0).toLocaleString("fr-FR")} € HT</div>
                      <div className="text-[9px] text-muted-foreground">Coût emp : {Number(d.cost_employer || 0).toLocaleString("fr-FR")} €</div>
                      <div className="flex gap-1 pt-0.5 flex-wrap">
                        {DECL_PHASES.filter(x => x.v !== p.v).slice(0, 2).map(t => (
                          <Button key={t.v} size="sm" variant="outline" className="h-5 px-1 text-[8px] flex-1" onClick={() => patchStatus(d, t.v)}>
                            → {t.label.split(" ")[0]}
                          </Button>
                        ))}
                      </div>
                      {p.v === "paie_envoyee" && (
                        <label className="flex items-center gap-1 text-[9px] cursor-pointer pt-0.5">
                          <Checkbox checked={!!d.invoice_paid} onCheckedChange={() => patchInvoice(d)} />
                          <span>Salaire payé</span>
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =============== SECTION 4 : Retex (3 canaux + photos par canal + lightbox) ================
function Lightbox({ items, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, onPrev, onNext]);
  if (index == null || !items?.[index]) return null;
  const cur = items[index];
  const downloadUrl = fileUrl(cur.file_id) + `?download=1&name=${encodeURIComponent(cur.name || "photo")}`;
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center" onClick={onClose} data-testid="retex-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 rounded-full bg-card hover:bg-card text-white p-2" data-testid="retex-lightbox-close"><X className="h-5 w-5" /></button>
      <a href={downloadUrl} download={cur.name || "photo"} onClick={e => e.stopPropagation()} className="absolute top-4 right-16 rounded-full bg-card hover:bg-card text-white p-2" title="Télécharger" data-testid="retex-lightbox-download"><Download className="h-5 w-5" /></a>
      {items.length > 1 && (
        <>
          <button onClick={e => { e.stopPropagation(); onPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-card hover:bg-card text-white p-3"><ChevronLeft className="h-6 w-6" /></button>
          <button onClick={e => { e.stopPropagation(); onNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-card hover:bg-card text-white p-3"><ChevronRight className="h-6 w-6" /></button>
        </>
      )}
      <img src={fileUrl(cur.file_id)} alt={cur.name} className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-card text-white text-xs px-3 py-1">
        {index + 1} / {items.length} · {cur.name}
      </div>
    </div>
  );
}

function RetexChannel({ event, reload, channel, title }) {
  const initial = (event.postprod?.retex || {})[`${channel}_feedback`] || "";
  const photos = (event.postprod?.retex || {})[`photos_${channel}`] || [];
  const [text, setText] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lbIdx, setLbIdx] = useState(null);
  useEffect(() => { setText(initial); setEditing(false); }, [event.id, initial]);

  const startEdit = () => setEditing(true);
  const cancelEdit = () => { setText(initial); setEditing(false); };
  const confirmSave = async () => {
    if (text === initial) { setEditing(false); return; }
    if (!confirm("Valider les modifications du retex ?")) return;
    setSaving(true);
    try {
      await api.patch(`/events/${event.id}/retex`, { [`${channel}_feedback`]: text });
      toast.success("Retex modifié");
      setEditing(false);
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };
  const uploadPhotos = async (fileList) => {
    if (!fileList?.length) return;
    try {
      const fd = new FormData();
      Array.from(fileList).forEach(f => fd.append("files", f));
      await api.post(`/events/${event.id}/retex/photos?channel=${channel}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${fileList.length} photo(s) ajoutée(s)`);
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };
  const deletePhoto = async (photoId) => {
    if (!confirm("Supprimer cette photo ?")) return;
    try { await api.delete(`/events/${event.id}/retex/photos/${photoId}?channel=${channel}`); await reload(true); }
    catch (e) { toast.error(extractError(e)); }
  };
  const openLb = (i) => setLbIdx(i);
  const closeLb = () => setLbIdx(null);
  const prevLb = () => setLbIdx(i => (i - 1 + photos.length) % photos.length);
  const nextLb = () => setLbIdx(i => (i + 1) % photos.length);

  return (
    <div className="space-y-2 rounded-xl border border-border/60 p-3" data-testid={`retex-${channel}`}>
      <div className="flex items-center gap-2">
        <Label className="text-xs font-semibold">{title}</Label>
        {!editing && (
          <Button size="sm" variant="outline" className="ml-auto rounded-full h-7 text-[11px]" onClick={startEdit} data-testid={`retex-${channel}-edit`}>
            <Pencil className="h-3 w-3 mr-1" /> Modifier
          </Button>
        )}
        {editing && (
          <div className="ml-auto flex gap-1.5">
            <Button size="sm" variant="ghost" className="rounded-full h-7 text-[11px]" onClick={cancelEdit}>Annuler</Button>
            <Button size="sm" className="rounded-full h-7 text-[11px] bg-primary" onClick={confirmSave} disabled={saving || text === initial} data-testid={`retex-${channel}-validate`}>
              <CheckCircle2 className="h-3 w-3 mr-1" /> Valider les modifications
            </Button>
          </div>
        )}
      </div>
      {editing ? (
        <Textarea rows={4} value={text} onChange={e => setText(e.target.value)} data-testid={`retex-${channel}-text`} autoFocus />
      ) : (
        <div className="min-h-[3.5rem] rounded-md border border-transparent bg-muted px-3 py-2 text-sm whitespace-pre-line" data-testid={`retex-${channel}-text-readonly`}>
          {text || <span className="italic text-muted-foreground">Aucun retex saisi — cliquez sur « Modifier » pour ajouter.</span>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-3 py-1 text-xs cursor-pointer hover:bg-primary/10">
          <Camera className="h-3.5 w-3.5 text-primary" />
          <span>Ajouter des photos</span>
          <input type="file" accept="image/*" multiple className="hidden" onChange={e => { uploadPhotos(e.target.files); e.target.value = ""; }} data-testid={`retex-${channel}-upload`} />
        </label>
        <Badge variant="outline" className="rounded-full text-[10px]">{photos.length} photo(s)</Badge>
      </div>
      {photos.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-1">
          {photos.map((p, i) => (
            <div key={p.id} className="relative group rounded-lg overflow-hidden border border-border/60 bg-muted aspect-square cursor-pointer" onClick={() => openLb(i)} data-testid={`retex-${channel}-photo-${p.id}`}>
              <img src={fileUrl(p.file_id)} alt={p.name} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <Maximize2 className="h-5 w-5 text-white drop-shadow" />
              </div>
              <a
                href={fileUrl(p.file_id) + `?download=1&name=${encodeURIComponent(p.name || "photo")}`}
                download={p.name || "photo"}
                onClick={e => e.stopPropagation()}
                className="absolute top-1 left-1 rounded-full bg-black/60 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Télécharger"
                data-testid={`retex-${channel}-download-${p.id}`}
              >
                <Download className="h-3 w-3" />
              </a>
              <button onClick={e => { e.stopPropagation(); deletePhoto(p.id); }} className="absolute top-1 right-1 rounded-full bg-black/60 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Supprimer">
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      {lbIdx != null && <Lightbox items={photos} index={lbIdx} onClose={closeLb} onPrev={prevLb} onNext={nextLb} />}
    </div>
  );
}
function RetexSection({ event, reload }) {
  return (
    <Card className="border-border/60 shadow-warm" data-testid="postprod-retex">
      <CardContent className="p-5 space-y-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
          <MessageSquare className="h-3 w-3 text-primary" /> Retex (retour d'expérience)
        </div>
        <RetexChannel event={event} reload={reload} channel="client" title="Retour client" />
        <RetexChannel event={event} reload={reload} channel="artists" title="Retours artistes" />
        <RetexChannel event={event} reload={reload} channel="aae" title="Retour équipe AAE / chargé de projet" />
      </CardContent>
    </Card>
  );
}

// =============== SECTION 5 : Clôture (inchangé) ================
function ClotureSection({ event, reload }) {
  const postprod = event.postprod || {};
  const isClosed = !!postprod.closed;
  const closeEvent = async () => {
    if (!confirm("Clôturer définitivement le dossier ? (l'étape « Événement clôturé » sera cochée et l'événement passera en Archivé)")) return;
    try { await api.post(`/events/${event.id}/close`); toast.success("Dossier clôturé 🎉"); await reload(true); }
    catch (e) { toast.error(extractError(e)); }
  };
  const reopenEvent = async () => {
    if (!confirm("Rouvrir le dossier ?")) return;
    try { await api.post(`/events/${event.id}/reopen`); toast.success("Dossier rouvert"); await reload(true); }
    catch (e) { toast.error(extractError(e)); }
  };
  return (
    <Card className={`border-2 shadow-warm ${isClosed ? "border-primary bg-primary/10" : "border-secondary/60 bg-secondary/10"}`}>
      <CardContent className="p-5">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Clôture du dossier</div>
            {isClosed ? (
              <div className="mt-2 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-brand text-lg text-primary">Événement clôturé</div>
                  <div className="text-xs text-muted-foreground">Le {postprod.closed_at} — étape « Événement clôturé » cochée automatiquement</div>
                </div>
              </div>
            ) : <div className="text-sm text-muted-foreground mt-1 italic">Dossier ouvert — cliquez pour clôturer une fois tout finalisé.</div>}
          </div>
          {isClosed ? (
            <Button variant="outline" size="sm" className="rounded-full" onClick={reopenEvent} data-testid="reopen-btn"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Rouvrir le dossier</Button>
          ) : (
            <Button size="lg" className="rounded-full bg-primary shadow-glow active:scale-95" onClick={closeEvent} data-testid="close-btn"><CheckCircle2 className="h-4 w-4 mr-2" /> Clôturer le dossier</Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// =============== ROOT COMPONENT ================
export default function EventPostprodTab({ event, reload }) {
  return (
    <div className="space-y-6" data-testid="postprod-tab">
      <ProviderInvoicesKanban event={event} reload={reload} />
      <DeclarationsMiniKanban event={event} reload={reload} />
      <ClientInvoiceSection event={event} reload={reload} />
      <RetexSection event={event} reload={reload} />
      <ClotureSection event={event} reload={reload} />
    </div>
  );
}
